import React, { useState, useEffect, useLayoutEffect } from "react";
import Papa from "papaparse";
import styles from "./ListView.module.css";

function ListView({ filter, regionFilter, onPlaceClick, selectedPlace }) {
  const [places, setPlaces] = useState([]);
  const [expandedPlaceId, setExpandedPlaceId] = useState(null);
  const [details, setDetails] = useState({});
  const [expandedTags, setExpandedTags] = useState({});

  useEffect(() => {
    fetch("/data/coordinate_with_tags_updated.geojson")
      .then((response) => response.json())
      .then((data) => {
        let filteredPlaces = data.features;

        if (filter) {
          filteredPlaces = filteredPlaces.filter((place) =>
            place.properties.common_Tag
              .toLowerCase()
              .includes(filter.toLowerCase())
          );
        }

        if (regionFilter) {
          filteredPlaces = filteredPlaces.filter((place) =>
            place.properties["Other Properties"].Address
              .toLowerCase()
              .includes(regionFilter.toLowerCase())
          );
        }

        setPlaces(filteredPlaces);
      })
      .catch((error) => console.error("Error loading GeoJSON:", error));
  }, [filter, regionFilter]);

  // 선택된 장소가 업데이트될 때 확장 및 스크롤 처리
  useLayoutEffect(() => {
    if (selectedPlace) {
      const placeId = selectedPlace.properties["Place ID"];
  
      // 선택된 항목 확장
      setExpandedPlaceId(placeId);
  
      // DOM 업데이트 후 스크롤
      setTimeout(() => {
        const listItem = document.getElementById(`place-${placeId}`);
        if (listItem) {
          listItem.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          console.error(`List item with ID "place-${placeId}" not found.`);
        }
      }, 0);
    } else {
      setExpandedPlaceId(null); // 선택이 없으면 모든 항목 축소
    }
  }, [selectedPlace]);

  const fetchDetails = (placeId) => {
    fetch("/data/detail_first.csv")
      .then((response) => response.text())
      .then((text) => {
        Papa.parse(text, {
          header: true,
          complete: (result) => {
            const groupedDetails = {};
            result.data.forEach((row) => {
              if (row["Place ID"] === placeId) {
                const tag = row.Tag;
                if (!groupedDetails[tag]) {
                  groupedDetails[tag] = [];
                }
                groupedDetails[tag].push(row);
              }
            });
            setDetails(groupedDetails);
          },
        });
      })
      .catch((error) => console.error("Error loading CSV:", error));
  };

  const handlePlaceClick = (place) => {
    const placeId = place.properties["Place ID"];
    if (expandedPlaceId === placeId) {
      setExpandedPlaceId(null);
      setDetails({});
    } else {
      setExpandedPlaceId(placeId);
      fetchDetails(placeId);
      onPlaceClick(place);
    }
  };
  const generateGoogleFormURL = (placeId, name) => {
    const formUrl = "https://docs.google.com/forms/d/11cPFL9qmi7Vjky0DiocnzFy8MO8SnI0WHHsHc_uK2MY/edit";
    const params = new URLSearchParams({
      "entry.123456789": placeId, // 여기에 Google Form 필드 ID를 입력하세요.
      "entry.987654321": name,    // 여기에 Google Form 필드 ID를 입력하세요.
    });
    return `${formUrl}?${params.toString()}`;
  };
  const toggleTag = (tag) => {
    setExpandedTags((prevState) => ({
      ...prevState,
      [tag]: !prevState[tag],
    }));
  };

  return (
    <div className={styles.sidePanel}>
      <ul className={styles.placeList}>
        {places.map((place) => {
          const placeId = place.properties["Place ID"];
          const isExpanded = expandedPlaceId === placeId;
          const isSelected = selectedPlace?.properties["Place ID"] === placeId; // 선택된 업체 확인
  
          return (
            <li
              key={placeId}
              id={`place-${placeId}`}
              className={`${styles.placeItem} ${isExpanded ? styles.expanded : ""} ${
                isSelected ? styles.selectedPlace : ""
              }`} // 선택된 항목에 추가 클래스 적용
              onClick={() => handlePlaceClick(place)}
            >
              <div className={styles.nameWithIcon}>
                <h3
                  className={`${isSelected ? styles.selectedPlaceName : ""}`} // 선택된 항목 이름 스타일링
                >
                  {place.properties["Other Properties"].Name}
                </h3>
                <a
                  href={generateGoogleFormURL(
                    placeId,
                    place.properties["Other Properties"].Name
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.flagIcon}
                  onClick={(e) => e.stopPropagation()} // 부모 클릭 이벤트 방지
                >
                  🚩
                </a>
              </div>
              <p>
                <strong>Treatment list:</strong> {place.properties["common_Tag"]}
              </p>
              <div className={styles.detailContent}>
                {isExpanded &&
                  Object.entries(details).map(([tag, entries]) => (
                    <div key={tag} className={styles.tagGroup}>
                      <h4
                        className={styles.tagTitle}
                        onClick={(e) => {
                          e.stopPropagation(); // 부모 클릭 이벤트 방지
                          toggleTag(tag);
                        }}
                      >
                        {tag || "Others"} {expandedTags[tag] ? "▼" : "►"}
                      </h4>
                      {expandedTags[tag] &&
                        entries.map((detail, detailIndex) => {
                          const formatPrice = (price) => {
                            if (!price) return "";
                            const formattedPrice = price.startsWith("$") ? price : `$${price}`;
                            return formattedPrice.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                          };

                          const startPrice = formatPrice(detail.Start_Price);
                          const fullPrice = formatPrice(detail.Full_Price);
                          const treatmentNumber = detail.Treatment_Number;

                          const startPriceText = startPrice
                            ? `${startPrice} per session`
                            : "";

                          let fullPriceText = "";
                          if (fullPrice) {
                            fullPriceText = treatmentNumber
                              ? `${fullPrice} per ${treatmentNumber} sessions`
                              : `${fullPrice} per session`;
                          }

                          return (
                            <div key={detailIndex} className={styles.detailContent}>
                              <p>
                                <strong>Treatment Section:</strong> {detail.Treatment_Section}
                              </p>
                              {startPriceText && (
                                <p>
                                  <strong>Start Price:</strong> {startPriceText}
                                </p>
                              )}
                              {fullPriceText && (
                                <p>
                                  <strong>Full Price:</strong> {fullPriceText}
                                </p>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ))}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default ListView;
